import {
  NODE_SCHEMAS_BY_TYPE,
  bindRawCanvasNode,
  type TimelineAudioConfigDict,
  type TimelineClipConfigDict,
  type TimelineConfigDict,
  type TimelineConfigDictYMap,
} from '../generated/nodeDataTypes'
import type { RawCanvasYNode as YMap, RawCanvasYNodeTable as YMapTable } from './useYCanvas'

type SeedSource = {
  nodeId: string
  outputPort: 'out_video' | 'out_audio'
  toIndex?: number
  ordinal: number
}

function yString(map: YMap, key: string): string | undefined {
  const value = map.get(key)
  return typeof value === 'string' && value ? value : undefined
}

function yNumber(map: YMap, key: string): number | undefined {
  const value = map.get(key)
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function inferredMediaOutput(yNodes: YMapTable, sourceId: string): 'out_video' | 'out_audio' | undefined {
  const source = yNodes.get(sourceId)
  const sourceType = source ? yString(source, 'type') : undefined
  const schema = sourceType ? NODE_SCHEMAS_BY_TYPE[sourceType] : undefined
  const mediaOutputs = schema?.outputs.filter(
    (output) => output.id === 'out_video' || output.id === 'out_audio',
  ) ?? []
  if (mediaOutputs.length !== 1) return undefined
  const outputId = mediaOutputs[0].id
  return outputId === 'out_video' || outputId === 'out_audio' ? outputId : undefined
}

function mediaKindForEdge(
  yNodes: YMapTable,
  edge: YMap,
): 'video' | 'audio' | undefined {
  const targetPort = yString(edge, 'to_port')
  if (targetPort === 'in_videos') return 'video'
  if (targetPort === 'in_audios' || targetPort === 'in_audio') return 'audio'
  if (targetPort !== undefined && targetPort !== 'in_reference_media') return undefined
  const sourceId = yString(edge, 'source')
  if (!sourceId) return undefined
  const sourcePort = yString(edge, 'from_port') ?? inferredMediaOutput(yNodes, sourceId)
  if (sourcePort === 'out_video') return 'video'
  if (sourcePort === 'out_audio') return 'audio'
  return undefined
}

function collectSeedSources(
  yNodes: YMapTable,
  yEdges: YMapTable,
  targetId: string,
): { videos: SeedSource[]; audios: SeedSource[] } {
  const videos: SeedSource[] = []
  const audios: SeedSource[] = []
  let ordinal = 0
  yEdges.forEach((edge) => {
    const currentOrdinal = ordinal
    ordinal += 1
    if (yString(edge, 'target') !== targetId) return
    const sourceId = yString(edge, 'source')
    const kind = mediaKindForEdge(yNodes, edge)
    if (!sourceId || !kind) return
    const outputPort: SeedSource['outputPort'] = kind === 'video' ? 'out_video' : 'out_audio'
    const source = {
      nodeId: sourceId,
      outputPort,
      toIndex: yNumber(edge, 'toIndex'),
      ordinal: currentOrdinal,
    }
    ;(kind === 'video' ? videos : audios).push(source)
  })
  const compare = (left: SeedSource, right: SeedSource) =>
    (left.toIndex ?? Number.MAX_SAFE_INTEGER) - (right.toIndex ?? Number.MAX_SAFE_INTEGER)
    || left.ordinal - right.ordinal
  videos.sort(compare)
  audios.sort(compare)
  return { videos, audios }
}

export function buildAutoSeededVideoEditorTimeline(
  videos: readonly SeedSource[],
  audios: readonly SeedSource[],
): TimelineConfigDict {
  const clips: TimelineClipConfigDict[] = videos.map((source, index) => ({
    id: `video-${index + 1}`,
    source_node_id: source.nodeId,
    source_path: `${source.nodeId}.${source.outputPort}`,
    order: index,
    track_index: 0,
    speed: 1,
    volume: 1,
    muted: false,
  }))
  const audioTracks: TimelineAudioConfigDict[] = audios.map((source, index) => ({
    id: `audio-${index + 1}`,
    source_node_id: source.nodeId,
    source_path: `${source.nodeId}.${source.outputPort}`,
    track_index: 0,
    audio_kind: 'audio',
    speed: 1,
    volume: 1,
    muted: false,
  }))
  return {
    operation: 'timeline_render',
    timeline: {
      clips,
      audio_tracks: audioTracks,
    },
  }
}

type AutoSeedNodeIds = { videos: string[]; audios: string[] }

function hasExactKeys(keys: IterableIterator<string>, expected: readonly string[]): boolean {
  const actual = Array.from(keys).sort()
  const sortedExpected = [...expected].sort()
  return actual.length === sortedExpected.length
    && actual.every((key, index) => key === sortedExpected[index])
}

function autoSeedNodeIds(config: TimelineConfigDictYMap): AutoSeedNodeIds | undefined {
  if (config.operation !== 'timeline_render') return undefined
  if (!hasExactKeys(config._raw.keys(), ['operation', 'timeline'])) return undefined
  const timeline = config.timeline
  if (!timeline || !hasExactKeys(timeline._raw.keys(), ['clips', 'audio_tracks'])) {
    return undefined
  }
  const clips = timeline.clips ?? []
  const audioTracks = timeline.audio_tracks
  if (!Array.isArray(audioTracks)) return undefined
  const videos: string[] = []
  const audios: string[] = []
  for (const [index, clip] of clips.entries()) {
    const nodeId = clip.source_node_id
    if (!nodeId || !hasExactKeys(clip._raw.keys(), [
      'id', 'source_node_id', 'source_path', 'order', 'track_index', 'speed', 'volume', 'muted',
    ])) return undefined
    if (
      clip.id !== `video-${index + 1}`
      || clip.source_path !== `${nodeId}.out_video`
      || clip.order !== index
      || clip.track_index !== 0
      || clip.speed !== 1
      || clip.volume !== 1
      || clip.muted !== false
    ) return undefined
    videos.push(nodeId)
  }
  for (const [index, track] of audioTracks.entries()) {
    const nodeId = track.source_node_id
    if (!nodeId || !hasExactKeys(track._raw.keys(), [
      'id', 'source_node_id', 'source_path', 'track_index', 'audio_kind', 'speed', 'volume', 'muted',
    ])) return undefined
    if (
      track.id !== `audio-${index + 1}`
      || track.source_path !== `${nodeId}.out_audio`
      || track.track_index !== 0
      || track.audio_kind !== 'audio'
      || track.speed !== 1
      || track.volume !== 1
      || track.muted !== false
    ) return undefined
    audios.push(nodeId)
  }
  return { videos, audios }
}

function matchesSources(current: AutoSeedNodeIds, videos: SeedSource[], audios: SeedSource[]): boolean {
  return current.videos.length === videos.length
    && current.audios.length === audios.length
    && current.videos.every((nodeId, index) => nodeId === videos[index].nodeId)
    && current.audios.every((nodeId, index) => nodeId === audios[index].nodeId)
}

export function syncAutoSeededVideoEditorTimeline(
  yNodes: YMapTable,
  yEdges: YMapTable,
  targetId: string,
): boolean {
  const target = yNodes.get(targetId)
  if (!target || yString(target, 'type') !== 'videoEditor') return false
  if (!target.has('in_video_config') || target.get('in_video_config') == null) return false
  const node = bindRawCanvasNode(target, 'video_editor')
  const current = node.get('in_video_config')
  if (!current) return false
  const currentSources = autoSeedNodeIds(current)
  if (!currentSources) return false
  const { videos, audios } = collectSeedSources(yNodes, yEdges, targetId)
  if (matchesSources(currentSources, videos, audios)) return false
  const next = buildAutoSeededVideoEditorTimeline(videos, audios)
  node.writeDataFields({ in_video_config: next })
  return true
}

export function syncAllAutoSeededVideoEditorTimelines(
  yNodes: YMapTable,
  yEdges: YMapTable,
): string[] {
  const changed: string[] = []
  yNodes.forEach((_node, nodeId) => {
    if (syncAutoSeededVideoEditorTimeline(yNodes, yEdges, nodeId)) changed.push(nodeId)
  })
  return changed
}
